import { useEffect, useRef } from 'react';

export interface Shortcut {
  /**
   * Key chord. Examples:
   *   - 'k'           — single key
   *   - '?'           — single non-alpha key
   *   - 'cmd+k'       — modifier + key (matches both Cmd and Ctrl on cross-platform)
   *   - 'g d'         — chord (press g, release, press d within 1.5s)
   *   - 'shift+/'     — shift modifier (note: ? on US keyboards already implies shift)
   *
   * Matched case-insensitively.
   */
  keys: string;
  /** What runs when the chord matches. Receives the originating event. */
  handler: (event: KeyboardEvent) => void;
  /** Human label, used by the help overlay. */
  description: string;
  /** Skip while typing in an input/textarea/contenteditable. Default true. */
  skipWhileTyping?: boolean;
  /** Prevent default for the matched event. Default true for chords/modifier
   *  combos, false for plain single keys (so '/' inside a normal page doesn't
   *  block forward-slash typing if there's a focused field somewhere weird). */
  preventDefault?: boolean;
}

const CHORD_TIMEOUT_MS = 1500;

interface ParsedKey {
  modifiers: { cmd: boolean; shift: boolean; alt: boolean };
  key: string;
}

interface ParsedShortcut {
  prefix: ParsedKey | null;
  main: ParsedKey;
}

function parseSingle(token: string): ParsedKey {
  const parts = token.toLowerCase().split('+').map(p => p.trim()).filter(Boolean);
  const mods = { cmd: false, shift: false, alt: false };
  let main = '';
  for (const p of parts) {
    if (p === 'cmd' || p === 'meta' || p === 'ctrl') mods.cmd = true;
    else if (p === 'shift') mods.shift = true;
    else if (p === 'alt' || p === 'option') mods.alt = true;
    else main = p;
  }
  return { modifiers: mods, key: main };
}

function parseShortcut(spec: string): ParsedShortcut {
  const tokens = spec.trim().split(/\s+/);
  if (tokens.length === 1) {
    return { prefix: null, main: parseSingle(tokens[0]) };
  }
  return { prefix: parseSingle(tokens[0]), main: parseSingle(tokens[1]) };
}

function eventMatches(p: ParsedKey, event: KeyboardEvent): boolean {
  const eKey = event.key.toLowerCase();
  if (eKey !== p.key) return false;
  // cmd|ctrl conflate cross-platform.
  const cmdLike = event.metaKey || event.ctrlKey;
  if (p.modifiers.cmd !== cmdLike) return false;
  if (p.modifiers.shift !== event.shiftKey) return false;
  if (p.modifiers.alt !== event.altKey) return false;
  return true;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

/**
 * Bind a list of keyboard shortcuts. The hook re-binds whenever the
 * shortcut list identity changes (memoize it in the caller if needed).
 *
 * Chords ('g d') are pressed serially within CHORD_TIMEOUT_MS.
 *
 * Reference impl from design-prompts/17-keyboard-shortcuts.md; the help
 * overlay + per-page registry context is queued for Claude design output.
 */
export function useKeyboardShortcuts(shortcuts: Shortcut[], enabled = true): void {
  const armedPrefixRef = useRef<{ key: ParsedKey; expiresAt: number } | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const onKey = (event: KeyboardEvent) => {
      // Reset expired prefix arm.
      const arm = armedPrefixRef.current;
      if (arm && arm.expiresAt < Date.now()) {
        armedPrefixRef.current = null;
      }
      const armedKey = armedPrefixRef.current?.key.key;

      // Pass 1: try to match — chord-completion first (only when armed),
      // then single-key shortcuts. Stop at first match.
      for (const shortcut of shortcuts) {
        const skipTyping = shortcut.skipWhileTyping !== false;
        if (skipTyping && isTypingTarget(event.target)) continue;

        const parsed = parseShortcut(shortcut.keys);
        if (parsed.prefix) {
          if (armedKey && armedKey === parsed.prefix.key && eventMatches(parsed.main, event)) {
            armedPrefixRef.current = null;
            if (shortcut.preventDefault !== false) event.preventDefault();
            shortcut.handler(event);
            return;
          }
        } else if (eventMatches(parsed.main, event)) {
          const isModified = parsed.main.modifiers.cmd || parsed.main.modifiers.alt;
          if (shortcut.preventDefault !== false && (isModified || shortcut.preventDefault === true)) {
            event.preventDefault();
          }
          shortcut.handler(event);
          return;
        }
      }

      // Pass 2: nothing matched as a single or chord-completion. Arm the
      // prefix for any chord shortcut that wants it.
      for (const shortcut of shortcuts) {
        const skipTyping = shortcut.skipWhileTyping !== false;
        if (skipTyping && isTypingTarget(event.target)) continue;
        const parsed = parseShortcut(shortcut.keys);
        if (parsed.prefix && eventMatches(parsed.prefix, event)) {
          armedPrefixRef.current = { key: parsed.prefix, expiresAt: Date.now() + CHORD_TIMEOUT_MS };
          return;
        }
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shortcuts, enabled]);
}
